import React from "react";
import { DefaultButton } from "@fluentui/react";
import { ContractOnboardingStrings } from "@/screen/Map/contractOnboarding.strings";
import { IntervenerRelation, isIdentitySelectableForContract } from "@/screen/ContractManagement/helpers/intervenerSelectionHelpers";
import type { IntervenerSelectionTableProps } from "@/screen/ContractManagement/Interfaces/IntervenerSelectionInterfaces";
import * as S from "@/screen/ContractManagement/Steps/IntervenerSelection/IntervenerSelectionTableStyles";

const IntervenerSelectionTable: React.FC<IntervenerSelectionTableProps> = ({userIdentities, hasAnyMinor, notifiedOwners, notifyingIdentityId, isChecked, isRelationLocked, onRelationChange, onNotifyOwner}) =>
{
    const strings = ContractOnboardingStrings;

    return(
        <S.TableWrap>
            <S.Table>
                <thead>
                    <S.TableRow>
                        <S.SelectHeader>No incluido en el contrato</S.SelectHeader>
                        <S.SelectHeader>Titular</S.SelectHeader>
                        <S.SelectHeader>Cotitular</S.SelectHeader>
                        <S.SelectHeader>Representante legal</S.SelectHeader>
                        <S.SelectHeader>Autorizado</S.SelectHeader>
                        <th>Nombre y apellidos</th>
                        <th>Correo electrónico</th>
                        <S.TightHeader>Menor de edad</S.TightHeader>
                        <S.TightHeader>Conocer a nuestro cliente (KYC)</S.TightHeader>
                    </S.TableRow>
                </thead>
                <S.TableBody>
                    {userIdentities.map((userIdentity, userIdentityIndex) =>
                    {
                        const isMinor = userIdentity.isMinor;
                        const selectable = isIdentitySelectableForContract(userIdentity);
                        // Un no seleccionable que ya estaba en el contrato (p. ej. dado de baja después) tiene que poder salir.
                        const canBeExcluded = selectable || !isChecked(userIdentity.id, undefined);
                        const radioName = `intervener-${userIdentityIndex}`;

                        return(
                            <S.TableRow key={`userIdentity-${userIdentity.id}`}>
                                <S.CenterCell $selected={isChecked(userIdentity.id, undefined)}>
                                    <input type="radio" name={radioName} value=""
                                        onChange={(event) => onRelationChange(event, userIdentity.id)}
                                        checked={isChecked(userIdentity.id, undefined)}
                                        disabled={!canBeExcluded || isRelationLocked(userIdentity.id, undefined)}
                                    />
                                </S.CenterCell>
                                <S.CenterCell $selected={isChecked(userIdentity.id, IntervenerRelation.Owner)}>
                                    <input type="radio" name={radioName} value={IntervenerRelation.Owner}
                                        onChange={(event) => onRelationChange(event, userIdentity.id)}
                                        checked={isChecked(userIdentity.id, IntervenerRelation.Owner)}
                                        disabled={!selectable || isRelationLocked(userIdentity.id, IntervenerRelation.Owner)}
                                    />
                                </S.CenterCell>
                                <S.CenterCell $selected={isChecked(userIdentity.id, IntervenerRelation.Cotitular)}>
                                    <input type="radio" name={radioName} value={IntervenerRelation.Cotitular}
                                        onChange={(event) => onRelationChange(event, userIdentity.id)}
                                        checked={isChecked(userIdentity.id, IntervenerRelation.Cotitular)}
                                        disabled={!selectable || isRelationLocked(userIdentity.id, IntervenerRelation.Cotitular)}
                                    />
                                </S.CenterCell>
                                <S.CenterCell $selected={isChecked(userIdentity.id, IntervenerRelation.LegalRepresentative)}>
                                    <input type="radio" name={radioName} value={IntervenerRelation.LegalRepresentative}
                                        onChange={(event) => onRelationChange(event, userIdentity.id)}
                                        checked={isChecked(userIdentity.id, IntervenerRelation.LegalRepresentative)}
                                        disabled={
                                            !selectable
                                            || isMinor
                                            || !hasAnyMinor
                                            || isRelationLocked(userIdentity.id, IntervenerRelation.LegalRepresentative)
                                        }
                                    />
                                </S.CenterCell>
                                <S.CenterCell $selected={isChecked(userIdentity.id, IntervenerRelation.Authorized)}>
                                    <input type="radio" name={radioName} value={IntervenerRelation.Authorized}
                                        onChange={(event) => onRelationChange(event, userIdentity.id)}
                                        checked={isChecked(userIdentity.id, IntervenerRelation.Authorized)}
                                        disabled={!selectable || isMinor || isRelationLocked(userIdentity.id, IntervenerRelation.Authorized)}
                                    />
                                </S.CenterCell>
                                <S.NameCell>
                                    {userIdentity.firstName} {userIdentity.lastName}{" "}
                                    {userIdentity.secondLastName}
                                </S.NameCell>
                                <td>{userIdentity.email}</td>
                                <S.MinorCell $isMinor={isMinor}>
                                    {isMinor ? "Sí" : "No"}
                                </S.MinorCell>
                                <S.KycCell>
                                    {userIdentity.hasValidKyc ? <span>Sí</span> : <span>No</span>}
                                    {userIdentity.canNotifyKycOwner === true &&
                                    (
                                        <S.NotifyOwnerWrap>
                                            {notifiedOwners[userIdentity.id] === undefined &&
                                            (
                                                <DefaultButton
                                                    text={strings.intervenerSelectionNotifyOwner}
                                                    title={strings.intervenerSelectionNotifyOwnerTitle}
                                                    disabled={notifyingIdentityId !== undefined}
                                                    onClick={() => onNotifyOwner(userIdentity.id)}
                                                />
                                            )}
                                            {notifiedOwners[userIdentity.id] === "sent" &&
                                            (
                                                <span>{strings.intervenerSelectionNotifySent}</span>
                                            )}
                                            {notifiedOwners[userIdentity.id] === "already" &&
                                            (
                                                <span>{strings.intervenerSelectionNotifyAlready}</span>
                                            )}
                                        </S.NotifyOwnerWrap>
                                    )}
                                </S.KycCell>
                            </S.TableRow>
                        );
                    })}
                </S.TableBody>
            </S.Table>
        </S.TableWrap>
    );
};

export default IntervenerSelectionTable;